import { useState, useMemo } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, Download, CompareArrows, ArrowBack, PushPin, PushPinOutlined } from '@mui/icons-material';
import { useSimulation } from '@/hooks/useSimulation';
import { useSimulationFilters } from '@/hooks/useSimulationFilters';
import type {
  SimulationResultDto,
  EntityChangeDto,
  RuleExecutionDto,
  SimulationEventDto,
} from '@/types/core/Simulation';
import { isAdvisory } from '@/types/core/Simulation';
import type { SimulationHistoryEntry } from '@/lib/simulationStore';

/**
 * One verdict chip for a rule row (C8): a rule the command proceeded through is a Warning, not
 * a Failed — the simulation panel must say what the command path did.
 */
function ruleVerdict(rule: Pick<RuleExecutionDto, 'passed' | 'severity'>): { label: string; color: 'success' | 'warning' | 'error' } {
  if (rule.passed) return { label: 'Passed', color: 'success' };
  if (isAdvisory(rule)) return { label: 'Warning', color: 'warning' };
  return { label: 'Failed', color: 'error' };
}

/**
 * Modal displaying simulation results with tabs for different categories of data.
 * Automatically opens when a simulation result is received.
 */
export function SimulationResultModal() {
  const { lastResult, clearResult, history, toggleSaveEntry } = useSimulation();
  const [activeTab, setActiveTab] = useState(0);
  const [compareTarget, setCompareTarget] = useState<SimulationHistoryEntry | null>(null);
  const [compareMenuAnchor, setCompareMenuAnchor] = useState<HTMLElement | null>(null);

  const filters = useSimulationFilters(lastResult?.entityChanges ?? []);

  if (!lastResult) return null;

  const result = lastResult;
  const originalError = extractOriginalError(result);

  // History entries excluding the current result (compare against previous)
  const comparableHistory = history.filter((h) => h.result !== result);

  return (
    <Dialog
      open
      onClose={clearResult}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">Simulation Result</Typography>
          <Chip
            label={`${result.requestMethod} ${result.requestPath}`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`${result.executionTimeMs}ms`}
            size="small"
            color="default"
          />
          {result.originalStatusCode >= 400 && (
            <Chip label={`Status ${result.originalStatusCode}`} size="small" color="error" />
          )}
          {result.includedDraftRules && (
            <Chip label="Draft Rules" size="small" color="info" />
          )}
          {!!result.errorMessage && (
            <Chip label="Error" size="small" color="error" />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Download JSON">
            <IconButton onClick={() => downloadResult(result)} size="small">
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={comparableHistory.length < 1 ? 'Run another simulation to compare' : 'Compare with previous'}>
            <span>
              <IconButton
                onClick={(e) => setCompareMenuAnchor(e.currentTarget)}
                size="small"
                disabled={comparableHistory.length < 1}
              >
                <CompareArrows fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <CompareMenu
            anchorEl={compareMenuAnchor}
            onClose={() => setCompareMenuAnchor(null)}
            history={comparableHistory}
            currentPath={result.requestPath}
            onSelect={(entry) => {
              setCompareTarget(entry);
              setCompareMenuAnchor(null);
            }}
            onToggleSave={(entry) => toggleSaveEntry(entry.timestamp)}
          />
          <IconButton onClick={clearResult} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0 }}>
        {compareTarget ? (
          <CompareView current={result} previous={compareTarget} onBack={() => setCompareTarget(null)} />
        ) : (
          <>
            {originalError && (
              <Alert severity="error" sx={{ m: 2, mb: 0 }}>
                <AlertTitle>{originalError.code || 'Request Failed'}</AlertTitle>
                {originalError.detail}
              </Alert>
            )}
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              <Tab label={`Changes (${filters.filteredChanges.length}/${result.entityChanges.length})`} />
              <Tab label={`Events (${result.eventsEmitted.length})`} />
              <Tab label={`Rules (${result.rulesExecuted.length})`} />
              <Tab label={`Workflows (${result.workflowsTriggered.length})`} />
              <Tab label="Summary" />
            </Tabs>

            {activeTab === 0 && <ChangesFilterBar filters={filters} />}

            <Box sx={{ p: 2, overflow: 'auto', maxHeight: '55vh' }}>
              {activeTab === 0 && <ChangesTab changes={filters.filteredChanges} />}
              {activeTab === 1 && <EventsTab events={result.eventsEmitted} />}
              {activeTab === 2 && <RulesTab rules={result.rulesExecuted} diagnostics={result.diagnostics ?? []} />}
              {activeTab === 3 && <WorkflowsTab workflows={result.workflowsTriggered} />}
              {activeTab === 4 && <SummaryTab result={result} />}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// --- Filter Bar ---

type FiltersResult = ReturnType<typeof useSimulationFilters>;

const CHANGE_TYPE_COLORS: Record<string, 'success' | 'warning' | 'error'> = {
  Added: 'success',
  Modified: 'warning',
  Deleted: 'error',
};

function ChangesFilterBar({ filters }: { filters: FiltersResult }) {
  const { changeTypeFilters, entityTypeFilters, toggleChangeType, toggleEntityType, counts } = filters;

  const sortedEntityTypes = useMemo(
    () => Object.keys(entityTypeFilters).sort(),
    [entityTypeFilters],
  );

  return (
    <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', flexDirection: 'column', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Type:
        </Typography>
        {Object.entries(changeTypeFilters).map(([type, visible]) => (
          <Chip
            key={type}
            label={`${type} (${counts.byChangeType[type] ?? 0})`}
            size="small"
            color={CHANGE_TYPE_COLORS[type] ?? 'default'}
            variant={visible ? 'filled' : 'outlined'}
            onClick={() => toggleChangeType(type)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Entity:
        </Typography>
        {sortedEntityTypes.map((type) => (
          <Chip
            key={type}
            label={`${type} (${counts.byEntityType[type] ?? 0})`}
            size="small"
            variant={entityTypeFilters[type] ? 'filled' : 'outlined'}
            onClick={() => toggleEntityType(type)}
            sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
          />
        ))}
      </Box>
    </Box>
  );
}

// --- Compare Menu (grouped by matching endpoint) ---

function CompareMenu({
  anchorEl,
  onClose,
  history,
  currentPath,
  onSelect,
  onToggleSave,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  history: SimulationHistoryEntry[];
  currentPath: string;
  onSelect: (entry: SimulationHistoryEntry) => void;
  onToggleSave: (entry: SimulationHistoryEntry) => void;
}) {
  const { matching, other } = useMemo(() => {
    const matching: SimulationHistoryEntry[] = [];
    const other: SimulationHistoryEntry[] = [];
    for (const entry of history) {
      if (entry.result.requestPath === currentPath) {
        matching.push(entry);
      } else {
        other.push(entry);
      }
    }
    return { matching, other };
  }, [history, currentPath]);

  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
      {matching.length > 0 && (
        <MenuItem disabled sx={{ opacity: '1 !important', py: 0.5 }}>
          <Typography variant="caption" color="text.secondary">Same endpoint</Typography>
        </MenuItem>
      )}
      {matching.map((entry, idx) => (
        <CompareMenuItem key={`m-${idx}`} entry={entry} onSelect={onSelect} onToggleSave={onToggleSave} />
      ))}
      {matching.length > 0 && other.length > 0 && <Divider />}
      {other.length > 0 && (
        <MenuItem disabled sx={{ opacity: '1 !important', py: 0.5 }}>
          <Typography variant="caption" color="text.secondary">Other endpoints</Typography>
        </MenuItem>
      )}
      {other.map((entry, idx) => (
        <CompareMenuItem key={`o-${idx}`} entry={entry} onSelect={onSelect} onToggleSave={onToggleSave} />
      ))}
    </Menu>
  );
}

function CompareMenuItem({
  entry,
  onSelect,
  onToggleSave,
}: {
  entry: SimulationHistoryEntry;
  onSelect: (entry: SimulationHistoryEntry) => void;
  onToggleSave: (entry: SimulationHistoryEntry) => void;
}) {
  return (
    <MenuItem onClick={() => onSelect(entry)} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2">{entry.label}</Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(entry.timestamp).toLocaleTimeString()} - {entry.result.executionTimeMs}ms
          {entry.result.originalStatusCode >= 400 ? ` (${entry.result.originalStatusCode})` : ''}
        </Typography>
      </Box>
      <Tooltip title={entry.saved ? 'Unpin' : 'Pin to keep'}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSave(entry);
          }}
          sx={{ color: entry.saved ? 'primary.main' : 'action.disabled', flexShrink: 0 }}
        >
          {entry.saved ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
        </IconButton>
      </Tooltip>
    </MenuItem>
  );
}

// --- Compare View ---

type CompareDetail = 'changes' | 'events' | 'rules' | 'workflows' | null;

function CompareView({
  current,
  previous,
  onBack,
}: {
  current: SimulationResultDto;
  previous: SimulationHistoryEntry;
  onBack: () => void;
}) {
  const prev = previous.result;
  const [detail, setDetail] = useState<CompareDetail>(null);

  const entityDiff = useMemo(() => {
    const currentTypes: Record<string, number> = {};
    const prevTypes: Record<string, number> = {};
    for (const c of current.entityChanges) currentTypes[c.entityType] = (currentTypes[c.entityType] ?? 0) + 1;
    for (const c of prev.entityChanges) prevTypes[c.entityType] = (prevTypes[c.entityType] ?? 0) + 1;

    const allTypes = [...new Set([...Object.keys(currentTypes), ...Object.keys(prevTypes)])].sort();
    return allTypes.map((type) => ({
      type,
      current: currentTypes[type] ?? 0,
      previous: prevTypes[type] ?? 0,
      delta: (currentTypes[type] ?? 0) - (prevTypes[type] ?? 0),
    }));
  }, [current, prev]);

  const toggle = (d: CompareDetail) => setDetail((prev) => (prev === d ? null : d));

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto', maxHeight: '70vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={onBack} size="small">
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1">Compare Results</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">Current</Typography>
          <Typography variant="body2">
            {current.requestMethod} {current.requestPath} - {current.executionTimeMs}ms
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">Previous ({new Date(previous.timestamp).toLocaleTimeString()})</Typography>
          <Typography variant="body2">
            {prev.requestMethod} {prev.requestPath} - {prev.executionTimeMs}ms
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Typography variant="subtitle2">Summary Comparison</Typography>
      <Typography variant="caption" color="text.secondary">Click a card to view details</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <CompareStatCard label="Changes" current={current.entityChanges.length} previous={prev.entityChanges.length} active={detail === 'changes'} onClick={() => toggle('changes')} />
        <CompareStatCard label="Events" current={current.eventsEmitted.length} previous={prev.eventsEmitted.length} active={detail === 'events'} onClick={() => toggle('events')} />
        <CompareStatCard label="Rules" current={current.rulesExecuted.length} previous={prev.rulesExecuted.length} active={detail === 'rules'} onClick={() => toggle('rules')} />
        <CompareStatCard
          label="Rules Passed"
          current={current.rulesExecuted.filter((r) => r.passed).length}
          previous={prev.rulesExecuted.filter((r) => r.passed).length}
          active={detail === 'rules'}
          onClick={() => toggle('rules')}
        />
        <CompareStatCard
          label="Rules Failed"
          current={current.rulesExecuted.filter((r) => !r.passed).length}
          previous={prev.rulesExecuted.filter((r) => !r.passed).length}
          active={detail === 'rules'}
          onClick={() => toggle('rules')}
        />
        <CompareStatCard label="Workflows" current={current.workflowsTriggered.length} previous={prev.workflowsTriggered.length} active={detail === 'workflows'} onClick={() => toggle('workflows')} />
        <CompareStatCard label="Time (ms)" current={current.executionTimeMs} previous={prev.executionTimeMs} />
      </Box>

      {detail === 'changes' && <CompareChangesDetail current={current} prev={prev} entityDiff={entityDiff} />}
      {detail === 'events' && <CompareEventsDetail current={current} prev={prev} />}
      {detail === 'rules' && <CompareRulesDetail current={current} prev={prev} />}
      {detail === 'workflows' && <CompareWorkflowsDetail current={current} prev={prev} />}

      {!detail && (
        <>
          <Divider />
          <Typography variant="subtitle2">Entity Changes by Type</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Entity Type</TableCell>
                  <TableCell align="right">Current</TableCell>
                  <TableCell align="right">Previous</TableCell>
                  <TableCell align="right">Delta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entityDiff.map((row) => (
                  <TableRow key={row.type}>
                    <TableCell>{row.type}</TableCell>
                    <TableCell align="right">{row.current}</TableCell>
                    <TableCell align="right">{row.previous}</TableCell>
                    <TableCell align="right">
                      <DeltaText delta={row.delta} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

// --- Compare Detail Panels ---

type EntityDiffRow = { type: string; current: number; previous: number; delta: number };

function CompareChangesDetail({
  current,
  prev,
  entityDiff,
}: {
  current: SimulationResultDto;
  prev: SimulationResultDto;
  entityDiff: EntityDiffRow[];
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Divider />
      <Typography variant="subtitle2">Entity Changes by Type</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Entity Type</TableCell>
              <TableCell align="right">Current</TableCell>
              <TableCell align="right">Previous</TableCell>
              <TableCell align="right">Delta</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entityDiff.map((row) => (
              <TableRow
                key={row.type}
                sx={{
                  bgcolor: row.delta > 0 ? 'success.50' : row.delta < 0 ? 'error.50' : undefined,
                }}
              >
                <TableCell>
                  {row.type}
                  {row.current > 0 && row.previous === 0 && (
                    <Chip label="new" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                  )}
                  {row.current === 0 && row.previous > 0 && (
                    <Chip label="removed" size="small" color="error" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                  )}
                </TableCell>
                <TableCell align="right">{row.current}</TableCell>
                <TableCell align="right">{row.previous}</TableCell>
                <TableCell align="right"><DeltaText delta={row.delta} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle2" sx={{ mt: 1 }}>Change Type Breakdown</Typography>
      <Box sx={{ display: 'flex', gap: 4 }}>
        <ChangeTypeBreakdown label="Current" changes={current.entityChanges} />
        <ChangeTypeBreakdown label="Previous" changes={prev.entityChanges} />
      </Box>
    </Box>
  );
}

function ChangeTypeBreakdown({ label, changes }: { label: string; changes: EntityChangeDto[] }) {
  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of changes) map[c.changeType] = (map[c.changeType] ?? 0) + 1;
    return map;
  }, [changes]);

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
        {['Added', 'Modified', 'Deleted'].map((type) => (
          <Chip
            key={type}
            label={`${type}: ${counts[type] ?? 0}`}
            size="small"
            color={CHANGE_TYPE_COLORS[type] ?? 'default'}
            variant="outlined"
          />
        ))}
      </Box>
    </Box>
  );
}

function CompareEventsDetail({ current, prev }: { current: SimulationResultDto; prev: SimulationResultDto }) {
  const diff = useMemo(() => {
    const currentEvents = new Map<string, number>();
    const prevEvents = new Map<string, number>();
    for (const e of current.eventsEmitted) currentEvents.set(e.eventType, (currentEvents.get(e.eventType) ?? 0) + 1);
    for (const e of prev.eventsEmitted) prevEvents.set(e.eventType, (prevEvents.get(e.eventType) ?? 0) + 1);

    const allTypes = [...new Set([...currentEvents.keys(), ...prevEvents.keys()])].sort();
    return allTypes.map((type) => ({
      type,
      current: currentEvents.get(type) ?? 0,
      previous: prevEvents.get(type) ?? 0,
      delta: (currentEvents.get(type) ?? 0) - (prevEvents.get(type) ?? 0),
    }));
  }, [current, prev]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Divider />
      <Typography variant="subtitle2">Events Comparison</Typography>
      {diff.length === 0 ? (
        <Typography color="text.secondary" variant="body2">No events in either run.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Event Type</TableCell>
                <TableCell align="right">Current</TableCell>
                <TableCell align="right">Previous</TableCell>
                <TableCell align="right">Delta</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {diff.map((row) => (
                <TableRow key={row.type}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {row.type}
                    {row.current > 0 && row.previous === 0 && (
                      <Chip label="new" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                    )}
                    {row.current === 0 && row.previous > 0 && (
                      <Chip label="removed" size="small" color="error" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                    )}
                  </TableCell>
                  <TableCell align="right">{row.current}</TableCell>
                  <TableCell align="right">{row.previous}</TableCell>
                  <TableCell align="right"><DeltaText delta={row.delta} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function CompareRulesDetail({ current, prev }: { current: SimulationResultDto; prev: SimulationResultDto }) {
  const diff = useMemo(() => {
    const currentRules = new Map(current.rulesExecuted.map((r) => [r.ruleKey, r]));
    const prevRules = new Map(prev.rulesExecuted.map((r) => [r.ruleKey, r]));
    const allKeys = [...new Set([...currentRules.keys(), ...prevRules.keys()])].sort();

    return allKeys.map((key) => ({
      key,
      current: currentRules.get(key) ?? null,
      previous: prevRules.get(key) ?? null,
    }));
  }, [current, prev]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Divider />
      <Typography variant="subtitle2">Rules Comparison</Typography>
      {diff.length === 0 ? (
        <Typography color="text.secondary" variant="body2">No rules executed in either run.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Rule Key</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="center">Current</TableCell>
                <TableCell align="center">Previous</TableCell>
                <TableCell>Change</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {diff.map((row) => {
                const statusChanged = row.current && row.previous && row.current.passed !== row.previous.passed;
                return (
                  <TableRow key={row.key} sx={statusChanged ? { bgcolor: 'warning.50' } : undefined}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {row.key}
                      {row.current && !row.previous && (
                        <Chip label="new" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                      )}
                      {!row.current && row.previous && (
                        <Chip label="removed" size="small" color="error" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                      )}
                      {(row.current?.isDraft || row.previous?.isDraft) && (
                        <Chip label="draft" size="small" color="info" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                      )}
                    </TableCell>
                    <TableCell>{row.current?.ruleName ?? row.previous?.ruleName ?? '-'}</TableCell>
                    <TableCell align="center">
                      {row.current ? (
                        <Chip label={ruleVerdict(row.current).label} size="small" color={ruleVerdict(row.current).color} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {row.previous ? (
                        <Chip label={ruleVerdict(row.previous).label} size="small" color={ruleVerdict(row.previous).color} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {statusChanged && (
                        <Typography variant="body2" sx={{ color: `${ruleVerdict(row.current!).color}.main`, fontWeight: 600 }}>
                          {ruleVerdict(row.previous!).label} → {ruleVerdict(row.current!).label}
                        </Typography>
                      )}
                      {row.current?.errorMessage && !row.current.passed && (
                        <Typography variant="caption" color={`${ruleVerdict(row.current).color}.main`} sx={{ display: 'block' }}>
                          {row.current.errorMessage}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function CompareWorkflowsDetail({ current, prev }: { current: SimulationResultDto; prev: SimulationResultDto }) {
  const diff = useMemo(() => {
    const currentSet = new Set(current.workflowsTriggered);
    const prevSet = new Set(prev.workflowsTriggered);
    const all = [...new Set([...currentSet, ...prevSet])].sort();

    return all.map((wf) => ({
      name: wf,
      inCurrent: currentSet.has(wf),
      inPrevious: prevSet.has(wf),
    }));
  }, [current, prev]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Divider />
      <Typography variant="subtitle2">Workflows Comparison</Typography>
      {diff.length === 0 ? (
        <Typography color="text.secondary" variant="body2">No workflows triggered in either run.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {diff.map((row) => (
            <Box key={row.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={row.name}
                variant="outlined"
                size="small"
                sx={{ fontFamily: 'monospace' }}
              />
              {row.inCurrent && !row.inPrevious && (
                <Chip label="new" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
              {!row.inCurrent && row.inPrevious && (
                <Chip label="removed" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
              {row.inCurrent && row.inPrevious && (
                <Chip label="unchanged" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function CompareStatCard({
  label,
  current,
  previous,
  active,
  onClick,
}: {
  label: string;
  current: number;
  previous: number;
  active?: boolean;
  onClick?: () => void;
}) {
  const delta = current - previous;
  const clickable = !!onClick;
  return (
    <Box
      onClick={onClick}
      sx={{
        border: 2,
        borderColor: active ? 'primary.main' : 'divider',
        borderRadius: 1,
        p: 1.5,
        minWidth: 120,
        textAlign: 'center',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': clickable
          ? { borderColor: 'primary.light', boxShadow: 1 }
          : undefined,
      }}
    >
      <Typography variant="h5">{current}</Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {delta !== 0 && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: delta > 0 ? 'success.main' : 'error.main' }}
        >
          {delta > 0 ? `+${delta}` : delta} vs prev
        </Typography>
      )}
    </Box>
  );
}

function DeltaText({ delta }: { delta: number }) {
  return (
    <Typography
      variant="body2"
      sx={{
        color: delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.secondary',
        fontWeight: delta !== 0 ? 600 : 400,
      }}
    >
      {delta > 0 ? `+${delta}` : delta === 0 ? '-' : delta}
    </Typography>
  );
}

// --- Tab Content ---

function ChangesTab({ changes }: { changes: EntityChangeDto[] }) {
  if (changes.length === 0) {
    return <Typography color="text.secondary">No entity changes match the current filters.</Typography>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Entity</TableCell>
            <TableCell>ID</TableCell>
            <TableCell>Change</TableCell>
            <TableCell>Properties</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {changes.map((change, idx) => (
            <TableRow key={idx}>
              <TableCell>{change.entityType}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {change.entityId?.slice(0, 8) ?? '-'}
              </TableCell>
              <TableCell>
                <Chip
                  label={change.changeType}
                  size="small"
                  color={
                    change.changeType === 'Added'
                      ? 'success'
                      : change.changeType === 'Deleted'
                        ? 'error'
                        : 'warning'
                  }
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.entries(change.propertyChanges).map(([prop, val]) => (
                    <Chip
                      key={prop}
                      label={`${prop}: ${formatValue(val.originalValue)} → ${formatValue(val.currentValue)}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function EventsTab({ events }: { events: SimulationEventDto[] }) {
  if (events.length === 0) {
    return <Typography color="text.secondary">No events emitted.</Typography>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Event Type</TableCell>
            <TableCell>Timestamp</TableCell>
            <TableCell>Data</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event, idx) => (
            <TableRow key={idx}>
              <TableCell sx={{ fontFamily: 'monospace' }}>{event.eventType}</TableCell>
              <TableCell>{new Date(event.timestamp).toLocaleTimeString()}</TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', fontSize: '0.7rem', maxWidth: 400, overflow: 'auto' }}
                >
                  {JSON.stringify(event.eventData, null, 2)?.slice(0, 200)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RulesTab({ rules, diagnostics }: { rules: RuleExecutionDto[]; diagnostics: string[] }) {
  const rulesDiagnostics = diagnostics.filter((d) => d.startsWith('[Rules]'));

  if (rules.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography color="text.secondary">No rules executed.</Typography>
        {rulesDiagnostics.map((d, idx) => (
          <Alert key={idx} severity="info" sx={{ mt: 1 }}>
            {d.replace('[Rules] ', '')}
          </Alert>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Rule Key</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Origin</TableCell>
            <TableCell>Result</TableCell>
            <TableCell>Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rules.map((rule, idx) => (
            <TableRow key={idx}>
              <TableCell sx={{ fontFamily: 'monospace' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {rule.ruleKey}
                  {rule.isDraft && (
                    <Chip label="Draft" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              </TableCell>
              <TableCell>{rule.ruleName}</TableCell>
              <TableCell>{rule.origin}</TableCell>
              <TableCell>
                <Chip
                  label={ruleVerdict(rule).label}
                  size="small"
                  color={ruleVerdict(rule).color}
                />
              </TableCell>
              <TableCell sx={{ color: isAdvisory(rule) ? 'warning.main' : 'error.main', fontSize: '0.8rem' }}>
                {rule.errorMessage || '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function WorkflowsTab({ workflows }: { workflows: string[] }) {
  if (workflows.length === 0) {
    return <Typography color="text.secondary">No workflows would be triggered.</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {workflows.map((wf, idx) => (
        <Chip key={idx} label={wf} variant="outlined" sx={{ justifyContent: 'flex-start' }} />
      ))}
    </Box>
  );
}

function SummaryTab({ result }: { result: SimulationResultDto }) {
  const passedRules = result.rulesExecuted.filter((r) => r.passed).length;
  // C8: an advisory rule the command proceeded through is a warning, not a failure
  const warnedRules = result.rulesExecuted.filter((r) => isAdvisory(r)).length;
  const failedRules = result.rulesExecuted.filter((r) => !r.passed && !isAdvisory(r)).length;
  const draftRules = result.rulesExecuted.filter((r) => r.isDraft).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2">
        {result.requestMethod} {result.requestPath} - Status {result.originalStatusCode}
      </Typography>

      {result.errorMessage && (
        <Typography color="error">{result.errorMessage}</Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <StatCard label="Entity Changes" value={result.entityChanges.length} />
        <StatCard label="Events Emitted" value={result.eventsEmitted.length} />
        <StatCard label="Rules Executed" value={result.rulesExecuted.length} />
        <StatCard label="Rules Passed" value={passedRules} color="success.main" />
        <StatCard label="Rules Failed" value={failedRules} color="error.main" />
        {warnedRules > 0 && <StatCard label="Rules Warned" value={warnedRules} color="warning.main" />}
        {draftRules > 0 && <StatCard label="Draft Rules" value={draftRules} color="info.main" />}
        <StatCard label="Workflows" value={result.workflowsTriggered.length} />
        <StatCard label="Execution Time" value={`${result.executionTimeMs}ms`} />
      </Box>

      {result.includedDraftRules && (
        <Typography variant="body2" color="info.main">
          Draft rules were included in this simulation. Draft rules replace active rules with the same key.
        </Typography>
      )}

      {result.diagnostics?.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2">Diagnostics</Typography>
          {result.diagnostics.map((d, idx) => (
            <Alert key={idx} severity="info" variant="outlined">
              {d}
            </Alert>
          ))}
        </Box>
      )}
    </Box>
  );
}

// --- Shared Components ---

function StatCard({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        minWidth: 120,
        textAlign: 'center',
      }}
    >
      <Typography variant="h5" sx={{ color: color || 'text.primary' }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

// --- Helpers ---

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return 'null';
  if (typeof val === 'string') return val.length > 30 ? val.slice(0, 30) + '...' : val;
  return String(val);
}

/** Extract error info from the originalResponse (ABP error format) */
function extractOriginalError(result: SimulationResultDto): { code?: string; detail: string } | null {
  if (result.originalStatusCode < 400) return null;

  const resp = result.originalResponse as any;
  if (!resp) return null;

  const err = resp.error;
  if (!err) return null;

  const detail = err.data?.message || err.message || 'Unknown error';
  return { code: err.code, detail };
}

function downloadResult(result: SimulationResultDto): void {
  const json = JSON.stringify(result, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const now = new Date();
  const ts = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const filename = `simulation-${result.requestMethod}-${ts}.json`;

  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}
